import { title } from 'node:process';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
  forwardRef,
} from '@angular/core';
import { CustomFile } from './fileTypes';
import { FormControl, NG_VALUE_ACCESSOR, Validators } from '@angular/forms';
import { ControlValueAccessorDirective } from '../../../Helpers/Directives/controlValueAccessor.directive';
import { TranslateService } from '@ngx-translate/core';
import { ValidationMessagesKeys } from '../../Validations/Configs/validationMessages.config';
import { FileVm } from '../../../../../@AppService/Models/fileVm';
import { CustomToasterService } from '../../../../../@AppService/Services/custom-toaster.service';
import JSZip from 'jszip';

@Component({
  selector: 'file-uploader',
  templateUrl: './fileUploader.component.html',
  styleUrls: ['./fileUploader.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FileUploaderFormComponent),
      multi: true,
    },
  ],
})
export class FileUploaderFormComponent
  extends ControlValueAccessorDirective
  implements OnChanges
{
  @ViewChild('fileDropRef') inputRef!: ElementRef;
  @Input() public label: string | null = null;
  @Input() title: string = '::shared:Attachments';
  @Input() isMultiple!: boolean;
  @Input() isfileRequired: boolean = false;
  @Input() numberOfAllowedFiles: number = 10;
  @Input() isSubmitted!: boolean;
  @Input() isViewMode!: boolean;
  @Input() maxSizeInMB: number = 10;
  @Input() allowedExtensions!: string[];
  @Input() notAllowedExtensionsMsg!: string;
  @Input() public control!: FormControl;
  @Input() public clear: boolean = false;

  @Input() public set previousUploadedFiles(files: CustomFile[]) {
    // Bind uploaded Files to Already Uploaded Array and Pending Files (Not Uploaded Yet) To  files Array To upload

    if (!!files && files.length) {
      this.previousFiles = files.filter(
        (file) => !(file instanceof File),
      ) as [];
      this.files = files.filter((file) => file instanceof File) as File[];
      setTimeout(() => {
        this.onChange(this.AllFiles);
      }, 0);
    }
  }
  showMaxSizeError: boolean = false;
  showNotAllowedExtension: boolean = false;
  files: any[] = [];
  previousFiles: FileVm[] = [];

  constructor(
    private translate: TranslateService,
    private customToasterService: CustomToasterService,
  ) {
    super();
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['allowedExtensions']) {
      this.allowedExtensions = changes['allowedExtensions'].currentValue;
      this.allowedExtensions = this.allowedExtensions.map((ext) =>
        ext.toLowerCase(),
      );
    }
    if (changes['clear']) {
      this.files = [];
    }
  }

  /**
   * on file drop handler
   */
  onFileDropped($event: any) {
    console.log('file Dropped');
    const fileArray = Array.from($event);
    this.prepareFilesList(fileArray);
  }

  /**
   * handle file from browsing
   */
  fileBrowseHandler(files: any) {
    this.prepareFilesList(files);
  }

  /**
   * Delete file from files list
   * @param index (File index)
   */
  deleteFile(index: number) {
    this.files.splice(index, 1);
    this.onChange(this.AllFiles);
  }

  /**
   * Simulate the upload process
   */
  uploadFilesSimulator(index: number) {
    setTimeout(() => {
      if (index === this.files.length) {
        return;
      } else {
        const progressInterval = setInterval(() => {
          if (this.files[index]?.progress === 100) {
            clearInterval(progressInterval);
            this.uploadFilesSimulator(index + 1);
          } else {
            if (this.files[index]) {
              this.files[index].progress += 5;
            }
          }
        }, 200);
      }
    }, 1000);
  }

  /**
   * Convert Files list to normal array list
   * @param files (Files List)
   */
  async prepareFilesList(files: Array<any>) {
    let filesArray: File[] = [];
    for (const file of files) {
      filesArray.push(file);
    }

    if (this.AllFiles?.length + files.length > this.numberOfAllowedFiles) {
      this.inputRef.nativeElement.value = null;
      this.customToasterService.showMessage({
        Title: this.translate.instant('toaster.error'),
        Message:
          this.translate.instant('toaster.allowedFileNumber') +
          ' ' +
          (Number(this.numberOfAllowedFiles) + 1),
        Type: 'error',
      });
      return;
    }
    if (this.isValidSize(filesArray) && this.isValidType(filesArray)) {
      for (const item of files) {
        item.progress = 0;
        if (await this.checkBinary(item)) {
          this.files.push(item);
        }
      }
      this.onChange(this.AllFiles);
      this.uploadFilesSimulator(0);
    }
  }

  /**  Validators */
  private isValidSize(files: Array<File>): boolean {
    if (files.every((file) => file.size < this.maxSizeInMB * 1024 * 1024))
      return true;
    else {
      this.customToasterService.showMessage({
        Title: this.translate.instant('toaster.error'),
        Message:
          this.translate.instant('toaster.allowedFileSize') +
          ' ' +
          this.maxSizeInMB +
          'MB',
        Type: 'error',
      });

      (this.inputRef.nativeElement as HTMLInputElement).value = '';
      this.showMaxSizeError = true;
      return false;
    }
  }

  private isValidType(files: Array<File>): boolean {
    let notAllowedExtension: string | undefined = '';
    if (
      files.every((file) => {
        const fileExtension = file.name.split('.').pop();
        if (
          fileExtension &&
          this.allowedExtensions.includes(fileExtension.toLowerCase())
        )
          return true;
        else {
          notAllowedExtension = fileExtension;
          this.customToasterService.showMessage({
            Title: this.translate.instant('toaster.error'),
            Message:
              notAllowedExtension +
              ' ' +
              this.translate.instant('toaster.allowedFileExtension') +
              ' ' +
              this.allowedExtensions.join(', '),
            Type: 'error',
          });
          this.showNotAllowedExtension = true;
          return false;
        }
      })
    )
      return true;
    else {
      (this.inputRef.nativeElement as HTMLInputElement).value = '';
      if (this.notAllowedExtensionsMsg) {
        this.showNotAllowedExtension = true;
      }
      return false;
    }
  }

  async checkBinary(file: File): Promise<boolean> {
    const arrBufferFile = await file.arrayBuffer();
    try {
      const arr = new Uint8Array(arrBufferFile).subarray(0, 4);
      let header = '';
      for (let i = 0; i < arr.length; i++) {
        header += arr[i].toString(16).padStart(2, '0'); // Ensure two digits per byte
      }

      const validMagicNumbers = {
        xls: ['d0cf11e0'],
        image: ['89504e47', 'ffd8ffe0', 'ffd8ffe1', 'ffd8ffe2', '47494638'],
        pdf: ['25504446'],
        docx: ['504b0304'],
      };
      // if(!header){

      //   return true
      // }

      if (
        validMagicNumbers.image.includes(header) ||
        validMagicNumbers.pdf.includes(header) ||
        validMagicNumbers.xls.includes(header)
      ) {
        return true;
      }

      if (validMagicNumbers.docx.includes(header)) {
        const fileContent = new Uint8Array(arrBufferFile);
        const zip = new JSZip();
        const zipContent = await zip.loadAsync(fileContent);
        // Check for the presence of specific files that indicate a DOCX structure
        const hasDocxStructure =
          zipContent.files['[Content_Types].xml'] &&
          (zipContent.files['word/document.xml'] ||
            zipContent.files['xl/workbook.xml'] ||
            zipContent.files['ppt/presentation.xml']);
        if (hasDocxStructure) {
          return true;
        }
      }

      this.customToasterService.showMessage({
        Type: 'error',
        Title: this.translate.instant('toaster.error'),
        Message: this.translate.instant('toaster.coruptedFile'),
      });
      return false;
    } catch (error) {
      console.error('Error checking binary file:', error);
      return false;
    }
  }

  /**
   * format bytes
   * @param bytes (File size in bytes)
   * @param decimals (Decimals point)
   */
  formatBytes(bytes: number, decimals: number) {
    if (bytes === 0) {
      return '0 Bytes';
    }
    const k = 1024;
    const dm = decimals <= 0 ? 0 : decimals || 2;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }

  public previewFileHandler(file: File) {
    let blob = new Blob([file]);
    let url = URL.createObjectURL(blob);
    let aElement = document.createElement('a');
    aElement.href = url;
    aElement.download = file.name;
    aElement.click();
    window.URL.revokeObjectURL(url);
  }

  public previewUploadedFileHandler(file: { path: any }) {
    //todo
    window.open(file.path, '_blank');
  }

  public deleteUploadedFile(file: { fileName: string }) {
    this.previousFiles = this.previousFiles.filter(
      (fileItem) => fileItem.fileName !== file.fileName,
    );
    this.onChange(this.AllFiles);
  }

  resetInput(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    if (inputElement) {
      inputElement.value = '';
    }
  }

  showFile(file: { path: string }) {
    window.open(file.path, '_blank'); // open in new window
  }

  /**
   * get show Input
   */
  public get isShowInput(): boolean {
    return (
      this.isMultiple ||
      (!this.isMultiple &&
        this.files &&
        this.files.length === 0 &&
        this.previousFiles.length === 0)
    );
  }

  public get AllFiles(): File[] {
    return this.files.length > 0 || this.previousFiles.length > 0
      ? [...this.files, ...this.previousFiles]
      : [];
  }

  public get errorMessage() {
    return ValidationMessagesKeys['requiredFile'].en;
  }

  public get ShowError(): boolean {
    if (!this.control) return false;
    const { dirty, touched } = this.control;
    return this.Invalid ? dirty || touched || this.isSubmitted : false;
  }

  public get Invalid(): boolean {
    return this.control.invalid || false;
  }

  public get IsRequired(): boolean {
    return this.control.hasValidator(Validators.required);
  }
}
